import { type FC, useMemo } from 'react'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, QueryClient, dehydrate } from '@tanstack/react-query'

import useInitialCookie from 'views/GrowthMasters/hooks/useInitialCookie'

import { GatedContentProvider } from 'contexts/gatedContent'

import { createCurriculumQuery, createFiltersQuery } from '@segment/api/routes/growthMasters/queries'
import { createClient } from '@segment/api/staticSiteBackend/client'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

import CurriculumView from '../../../views/GrowthMasters/CurriculumPage'

const Curriculum: FC<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])
  const [showContent, isLoading, updateRegistered] = useInitialCookie()

  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <GatedContentProvider value={{ showContent, updateRegistered }}>
        {isLoading ? null : <CurriculumView />}
      </GatedContentProvider>
    </StaticSiteBackendProvider>
  )
}

export const getStaticProps: GetStaticProps<AppExternalPageProps> = async () => {
  const client = createClient()
  const queryClient = new QueryClient()

  await queryClient.prefetchInfiniteQuery(...createCurriculumQuery(client, { lessonTopics: [] }), {
    staleTime: Infinity,
  })

  await queryClient.prefetchQuery(...createFiltersQuery(client), { staleTime: Infinity })

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps = {
    dehydratedState: safeDehydratedState,
  }

  return {
    props,
  }
}

export default Curriculum
